import { palette, spaces } from '@kimuno/theme';
import { IconButton, SvgIcon } from '@mui/material';
import { useTranslation } from 'react-i18next';

import getMuiIcon from '../../../../../utils/getMuiIcon';
import Tooltip from '../../../../Tooltip';

type DictationButtonProps = {
	mode: 'start' | 'cancel';
	onClick: () => void;
	disabled?: boolean;
	/** Dictation was requested and the browser has not started it yet. */
	pressed?: boolean;
};

/**
 * @storybook-internal The prompt input's microphone; only meaningful inside AIAssistantFooter.
 *
 * Both modes render the same button, so keyboard focus stays on it when the microphone turns
 * into the X. For the same reason the pressed microphone is `aria-disabled` rather than
 * disabled: a disabled button would drop the focus while the browser starts recognition.
 */
const DictationButton = ({
	mode,
	onClick,
	disabled = false,
	pressed = false,
}: DictationButtonProps) => {
	const { t } = useTranslation();
	const isStart = mode === 'start';
	const label = isStart
		? t('Speak your text here, we will transcribe it for you.')
		: t('Cancel dictation');
	const isInactive = disabled || pressed;

	return (
		<Tooltip label={label} hideWithoutOverflow={false}>
			<IconButton
				type="button"
				data-testid="ai-assistant-dictation-button"
				sx={{ padding: spaces.xxs }}
				disabled={disabled}
				aria-label={label}
				aria-pressed={isStart ? pressed : undefined}
				aria-disabled={pressed || undefined}
				onClick={pressed ? undefined : onClick}
			>
				<SvgIcon
					sx={{
						width: 24,
						height: 24,
						color: isInactive ? palette.action.disabled : palette.primary.main,
						// The microphone's paths carry their own fill.
						'& path': { fill: 'currentColor' },
					}}
					viewBox={isStart ? '0 0 16 16' : '0 0 24 24'}
					component={getMuiIcon(isStart ? 'microphone' : 'close')}
					aria-hidden="true"
				/>
			</IconButton>
		</Tooltip>
	);
};

export default DictationButton;
